import { createAsyncAction, createAction } from "typesafe-actions";
import {
  SensorDataSessionItemList,
  SensorDataSession,
  SensorDataSessionMarker,
  SensorDataSessionMarkerDot,
} from "@shared/models";
import {
  Filter,
  PaginatedResponse,
  SensorDataSessionMarkerFilter,
  SensorDataSessionValuesFilter,
  SensorDataSessionProcessedValuesFilter,
  SensorDataSessionFilter,
  WithCallback,
} from "@shared/interfaces";

import { SensorDataSessionActionTypes } from "./constants";
import {
  CreateUpdateSensorDataSessionSuccess,
  CreateSensorDataSessionMarkerDotSuccess,
  DeleteSensorDataSessionSuccess,
  SensorDataSessionValues,
  CreateSensorDataSessionMarkerDto,
  CreateSensorDataSessionMarkerSuccess,
  CreateSensorDataSessionMarkerDotDto,
  DeleteSensorDataSessionMarkerDotDto,
  DownloadSensorDataSessionValuesDto,
  DeleteSensorDataSessionMarkerDotSuccess,
  DeleteSensorDataSessionMarkerDto,
  DeleteSensorDataSessionMarkerSuccess,
  SensorDataSessionProcessedValues,
} from "../interfaces";

export const getSensorDataSessions = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSIONS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSIONS_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSIONS_FAILURE,
)<SensorDataSessionFilter, PaginatedResponse<SensorDataSessionItemList>, Error>();

export const getSensorDataSessionValues = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_VALUES,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_VALUES_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_VALUES_FAILURE,
)<SensorDataSessionValuesFilter & { id: number }, SensorDataSessionValues[], Error>();

export const getSensorDataSessionProcessedValues = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_PROCESSED_VALUES,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_PROCESSED_VALUES_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_PROCESSED_VALUES_FAILURE,
)<SensorDataSessionProcessedValuesFilter & { id: number }, SensorDataSessionProcessedValues[], Error>();

export const createSensorDataSession = createAsyncAction(
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_SUCCESS,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_FAILURE,
)<WithCallback<{ data: FormData }>, CreateUpdateSensorDataSessionSuccess, Error>();

export const getSensorDataSession = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_FAILURE,
)<{ id: number }, SensorDataSession | null, Error>();

export const downloadSensorDataSessionValues = createAsyncAction(
  SensorDataSessionActionTypes.DOWNLOAD_SENSOR_DATA_SESSION_VALUES,
  SensorDataSessionActionTypes.DOWNLOAD_SENSOR_DATA_SESSION_VALUES_SUCCESS,
  SensorDataSessionActionTypes.DOWNLOAD_SENSOR_DATA_SESSION_VALUES_FAILURE,
)<DownloadSensorDataSessionValuesDto, string, Error>();

export const deleteSensorDataSession = createAsyncAction(
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_SUCCESS,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_FAILURE,
)<{ id: number }, DeleteSensorDataSessionSuccess, Error>();

export const updateSensorDataSession = createAsyncAction(
  SensorDataSessionActionTypes.UPDATE_SENSOR_DATA_SESSION,
  SensorDataSessionActionTypes.UPDATE_SENSOR_DATA_SESSION_SUCCESS,
  SensorDataSessionActionTypes.UPDATE_SENSOR_DATA_SESSION_FAILURE,
)<WithCallback<{ id: number; data: FormData }>, CreateUpdateSensorDataSessionSuccess, Error>();

export const createSensorDataSessionMarker = createAsyncAction(
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER_SUCCESS,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER_FAILURE,
)<CreateSensorDataSessionMarkerDto, CreateSensorDataSessionMarkerSuccess, Error>();

export const deleteSensorDataSessionMarker = createAsyncAction(
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER_SUCCESS,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER_FAILURE,
)<DeleteSensorDataSessionMarkerDto, DeleteSensorDataSessionMarkerSuccess, Error>();

export const createSensorDataSessionMarkerDot = createAsyncAction(
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER_DOT,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER_DOT_SUCCESS,
  SensorDataSessionActionTypes.CREATE_SENSOR_DATA_SESSION_MARKER_DOT_FAILURE,
)<WithCallback<CreateSensorDataSessionMarkerDotDto>, CreateSensorDataSessionMarkerDotSuccess, Error>();

export const getSensorDataSessionMarkers = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_MARKERS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_MARKERS_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_MARKERS_FAILURE,
)<SensorDataSessionMarkerFilter, SensorDataSessionMarker[], Error>();

export const getSensorDataSessionDots = createAsyncAction(
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_DOTS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_DOTS_SUCCESS,
  SensorDataSessionActionTypes.GET_SENSOR_DATA_SESSION_DOTS_FAILURE,
)<{ id: number; files?: number[]; station?: string }, SensorDataSessionMarkerDot[], Error>();

export const deleteSensorDataSessionDot = createAsyncAction(
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER_DOT,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER_DOT_SUCCESS,
  SensorDataSessionActionTypes.DELETE_SENSOR_DATA_SESSION_MARKER_DOT_FAILURE,
)<DeleteSensorDataSessionMarkerDotDto, DeleteSensorDataSessionMarkerDotSuccess, Error>();

export const setFilter = createAction(SensorDataSessionActionTypes.SET_FILTER)<Filter | null>();
export const clearSensorDataSessions = createAction(SensorDataSessionActionTypes.CLEAR_SENSOR_DATA_SESSIONS)();
export const setFilesPercentage = createAction(SensorDataSessionActionTypes.SET_FILES_UPLOADING_PERCENTAGE)<
  number | null
>();
export const setCurrentSessionValuesFilter = createAction(
  SensorDataSessionActionTypes.SET_CURRENT_SESSION_VALUES_FILTER,
)<SensorDataSessionValuesFilter | null>();
